import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Async from 'react-select/async';
import { toast } from 'react-toastify';

import { API } from '../../../API';
import { Loader } from '../../../components/Loader/Loader';
import Modal from '../../../components/Modal';
import Switch from '../../../components/Switch';
import { AppAccess } from '../adminTypes';

import styles from './AccessManager.module.scss';

interface WorkspaceOption {
  value: number;
  label: string;
}

interface WorkspaceSummary {
  id: number;
  name: string;
}

interface AccessManagerProps {
  appId: string;
}

export const AccessManager: React.FC<AccessManagerProps> = ({ appId }) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<AppAccess[] | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] =
    useState<WorkspaceOption | null>(null);
  const [granting, setGranting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const [revokeCandidate, setRevokeCandidate] = useState<AppAccess | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);

  const fetchAccess = useCallback(() => {
    API.fetch('GET', `/API/v2/internal/windmill/apps/${appId}/access`)
      .then((data: AppAccess[]) => {
        setEntries(data);
        setLoadError(null);
      })
      .catch((err: Error) => {
        setLoadError(err.message);
        setEntries([]);
      });
  }, [appId]);

  useEffect(() => {
    fetchAccess();
    API.fetch('GET', '/API/v1/workspaces?size=100')
      .then((data: { objects?: WorkspaceSummary[] } | WorkspaceSummary[]) => {
        const list = Array.isArray(data) ? data : data.objects || [];
        setWorkspaces(list);
        setWorkspaceError(null);
      })
      .catch((err: Error) => {
        setWorkspaces([]);
        setWorkspaceError(err.message);
      });
  }, [fetchAccess]);

  useEffect(
    () => () => {
      if (searchTimeoutRef.current !== null) {
        clearTimeout(searchTimeoutRef.current);
      }
    },
    [],
  );

  const handleGrant = useCallback(() => {
    const workspaceId = selectedWorkspace?.value;
    if (!workspaceId) {
      toast.error(t('Select a workspace to grant access') as string);
      return;
    }
    setGranting(true);
    API.fetch('POST', `/API/v2/internal/windmill/apps/${appId}/access`, null, {
      workspace_id: workspaceId,
      is_enabled: true,
    })
      .then(() => {
        toast.success(t('Access granted') as string);
        setSelectedWorkspace(null);
        fetchAccess();
      })
      .catch((err: Error) => {
        toast.error(err.message);
      })
      .finally(() => setGranting(false));
  }, [appId, fetchAccess, selectedWorkspace, t]);

  const handleToggle = useCallback(
    (entry: AppAccess) => {
      setBusyId(entry.id);
      API.fetch(
        'PATCH',
        `/API/v2/internal/windmill/apps/${appId}/access/${entry.workspace_id}`,
        null,
        { is_enabled: !entry.is_enabled },
      )
        .then(() => {
          fetchAccess();
        })
        .catch((err: Error) => {
          toast.error(err.message);
        })
        .finally(() => setBusyId(null));
    },
    [appId, fetchAccess],
  );

  const confirmRevoke = useCallback(() => {
    if (!revokeCandidate) return;
    const entry = revokeCandidate;
    setBusyId(entry.id);
    API.fetch(
      'DELETE',
      `/API/v2/internal/windmill/apps/${appId}/access/${entry.workspace_id}`,
    )
      .then(() => {
        toast.success(t('Access revoked') as string);
        fetchAccess();
      })
      .catch((err: Error) => {
        toast.error(err.message);
      })
      .finally(() => {
        setBusyId(null);
        setRevokeCandidate(null);
      });
  }, [appId, fetchAccess, revokeCandidate, t]);

  const grantedWorkspaceIds = new Set(
    (entries ?? []).map((e) => e.workspace_id),
  );

  const defaultOptions: WorkspaceOption[] = workspaces
    .filter((w) => !grantedWorkspaceIds.has(w.id))
    .sort((a, b) => (a.name > b.name ? 1 : -1))
    .map((w) => ({ value: w.id, label: w.name }));

  const loadOptions = (
    inputValue: string,
    callback: (options: WorkspaceOption[]) => void,
  ): void => {
    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current);
    }
    // Bump the sequence whether or not we're going to fire a request, so any
    // in-flight older response is invalidated even when the user clears input.
    const seq = ++searchSeqRef.current;
    if (!inputValue) {
      callback(defaultOptions);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      API.fetch(
        'GET',
        `/API/v1/workspaces?q=${encodeURIComponent(inputValue)}`,
      )
        .then((data: { objects?: WorkspaceSummary[] } | WorkspaceSummary[]) => {
          if (seq !== searchSeqRef.current) return;
          const list = Array.isArray(data) ? data : data.objects || [];
          const options = list
            .filter((w) => !grantedWorkspaceIds.has(w.id))
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            .map((w) => ({ value: w.id, label: w.name }));
          callback(options);
        })
        .catch(() => {
          if (seq !== searchSeqRef.current) return;
          callback([]);
        });
    }, 1000);
  };

  if (!entries) return <Loader />;

  return (
    <div className={styles.root}>
      <div className={styles.grantBar}>
        <div className={styles.workspacePicker}>
          <Async<WorkspaceOption>
            defaultOptions={defaultOptions}
            value={selectedWorkspace}
            onChange={(value) =>
              setSelectedWorkspace((value as WorkspaceOption | null) ?? null)
            }
            loadOptions={loadOptions}
            placeholder={
              workspaceError
                ? workspaceError
                : (t('Select workspace to grant access...') as string)
            }
            noOptionsMessage={() =>
              t('No matching workspaces') as string
            }
            loadingMessage={() => t('Loading...') as string}
            isClearable
          />
        </div>
        <button
          type="button"
          className="button"
          onClick={handleGrant}
          disabled={granting || !selectedWorkspace}
        >
          {granting ? t('Granting...') : t('Grant access')}
        </button>
      </div>

      {workspaceError && (
        <div className={styles.error}>{workspaceError}</div>
      )}
      {loadError && <div className={styles.error}>{loadError}</div>}

      {entries.length === 0 ? (
        <div className={styles.empty}>
          {t('No workspaces have access yet.')}
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">{t('Workspace')}</th>
              <th scope="col">{t('Granted by')}</th>
              <th scope="col">{t('Granted')}</th>
              <th scope="col">{t('Enabled')}</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td>
                  <strong>
                    {entry.workspace_name ?? `#${entry.workspace_id}`}
                  </strong>
                  <div className={styles.dim}>#{entry.workspace_id}</div>
                </td>
                <td>
                  {entry.granted_by_name ?? `#${entry.granted_by}`}
                </td>
                <td className={styles.dim}>
                  {new Date(entry.granted_at).toLocaleDateString()}
                </td>
                <td>
                  <Switch
                    id={`access-switch-${entry.id}`}
                    value={entry.is_enabled}
                    onChange={() => handleToggle(entry)}
                    isDisabled={busyId === entry.id}
                  />
                </td>
                <td className={styles.actions}>
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={() => setRevokeCandidate(entry)}
                    disabled={busyId === entry.id}
                  >
                    <i className="las la-trash" /> {t('Revoke')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal
        isVisible={Boolean(revokeCandidate)}
        title={t('Revoke')}
        content={<p>{t('Revoke access for this workspace?')}</p>}
        footer={
          <>
            <button className="btn-danger" onClick={confirmRevoke}>
              {t('Revoke')}
            </button>
            <button
              className="btn-reset"
              onClick={() => setRevokeCandidate(null)}
            >
              {t('Cancel')}
            </button>
          </>
        }
        onClose={() => setRevokeCandidate(null)}
        className={undefined}
      />
    </div>
  );
};
